import { $, $$, OverlayElement, announce, debounce, fetchHTML, theme } from './utils.js';

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

const mobile = window.matchMedia('(max-width: 820px)');

function withSection(url, sectionId) {
  return `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}`;
}

/* Filters + sort: Storefront Filtering via the Section Rendering API, URL stays shareable. */
class FacetForm extends OverlayElement {
  connectedCallback() {
    super.connectedCallback();
    if (this._facets) return;
    this._facets = true;
    this.sectionId = this.dataset.sectionId;
    this.form = $('[data-facet-form]', this);
    this.queue = debounce(() => this.update(), 350);

    this.form.addEventListener('change', () => this.queue());
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.update();
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-facet-remove]');
      if (remove) {
        event.preventDefault();
        this.load(remove.href);
        return;
      }
      const toggle = event.target.closest('[data-facets-open]');
      if (toggle) {
        this.panelEl().setAttribute('role', 'dialog');
        this.panelEl().setAttribute('aria-modal', 'true');
        this.panelEl().setAttribute('aria-label', toggle.textContent.trim());
        this.panelEl().tabIndex = -1;
        this.open(toggle);
      }
    });
    // desktop dropdown panels: one open at a time, close on outside click and Esc
    this.addEventListener(
      'toggle',
      (event) => {
        if (mobile.matches || !event.target.open) return;
        $$('details.facet[open]', this).forEach((other) => other !== event.target && other.removeAttribute('open'));
      },
      true
    );
    document.addEventListener('click', (event) => {
      if (mobile.matches) return;
      $$('details.facet[open]', this).forEach((details) => !details.contains(event.target) && details.removeAttribute('open'));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || mobile.matches) return;
      const open = $('details.facet[open]', this);
      if (open) {
        open.removeAttribute('open');
        $('summary', open).focus();
      }
    });
    // close the drawer when the viewport grows back to desktop
    mobile.addEventListener('change', () => !mobile.matches && this.close());
    window.addEventListener('popstate', () => this.load(window.location.href, false));
  }

  panelEl() {
    return $('.facets__panel', this);
  }

  get panel() {
    return this.panelEl();
  }

  close() {
    super.close();
    const panel = this.panelEl();
    if (panel) ['role', 'aria-modal', 'aria-label'].forEach((attr) => panel.removeAttribute(attr));
  }

  results() {
    return document.getElementById(`results-${this.sectionId}`);
  }

  update() {
    const params = new URLSearchParams();
    new FormData(this.form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    const action = this.form.getAttribute('action');
    const query = params.toString();
    this.load(query ? `${action}?${query}` : action);
  }

  async load(url, push = true) {
    const results = this.results();
    if (!results) {
      window.location.href = url;
      return;
    }
    results.classList.add('is-loading');
    try {
      const doc = await fetchHTML(withSection(url, this.sectionId));
      const nextResults = $('[data-results-inner]', doc);
      if (nextResults) $('[data-results-inner]', results).innerHTML = nextResults.innerHTML;
      $('[data-active-filters]', this).innerHTML = $('[data-active-filters]', doc).innerHTML;
      $('[data-count]', this).textContent = $('[data-count]', doc).textContent;
      this.sync(doc);
      if (push) window.history.pushState({}, '', url);
      announce($('[data-count]', doc).textContent);
    } catch (error) {
      window.location.href = url;
    } finally {
      results.classList.remove('is-loading');
    }
  }

  /* Update counts / disabled / checked states in place so focus and open panels are preserved */
  sync(doc) {
    $$('details.facet', this).forEach((details) => {
      const next = doc.querySelector(`[data-facet-id="${CSS.escape(details.dataset.facetId)}"]`);
      if (!next) return;
      $('summary', details).innerHTML = $('summary', next).innerHTML;
      $$('input', details).forEach((input) => {
        const match = input.id ? doc.getElementById(input.id) : null;
        if (match) {
          input.disabled = match.disabled;
          input.checked = match.checked;
          const count = $('[data-facet-count]', input.closest('label'));
          const nextCount = $('[data-facet-count]', match.closest('label'));
          if (count && nextCount) count.textContent = nextCount.textContent;
        } else if (input.type === 'number') {
          const twin = $(`input[name="${CSS.escape(input.name)}"]`, next);
          if (twin) input.value = twin.value;
        }
      });
    });
    const toggle = $('[data-facets-open]', this);
    const nextToggle = $('[data-facets-open]', doc);
    if (toggle && nextToggle) toggle.textContent = nextToggle.textContent;
    const apply = $('.facets__foot .btn', this);
    const nextApply = $('.facets__foot .btn', doc);
    if (apply && nextApply) apply.textContent = nextApply.textContent;
  }
}
define('facet-form', FacetForm);

/* Progressive "Load more" over standard pagination */
class ProductResults extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-load-more]');
      if (button) this.more(button);
    });
    this.enhance();
    new MutationObserver(() => this.enhance()).observe(this, { childList: true, subtree: true });
  }

  enhance() {
    const pagination = $('[data-pagination]', this);
    if (pagination && $('[data-load-more]', this)) pagination.hidden = true;
  }

  async more(button) {
    button.classList.add('is-loading');
    button.disabled = true;
    try {
      const doc = await fetchHTML(withSection(button.dataset.nextUrl, this.sectionId));
      const grid = $('[data-grid]', this);
      const incoming = $$('[data-grid] > *', doc);
      incoming.forEach((node) => grid.append(node));
      const nextButton = $('[data-load-more]', doc);
      const shown = $('[data-shown]', doc);
      if (shown) $('[data-shown]', this).textContent = shown.textContent;
      if (nextButton) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl;
        button.disabled = false;
        button.classList.remove('is-loading');
      } else {
        button.remove();
      }
      const firstNew = incoming[0] && $('a', incoming[0]);
      if (firstNew) firstNew.focus({ preventScroll: true });
      announce(shown ? shown.textContent : '');
    } catch (error) {
      window.location.href = button.dataset.nextUrl;
    }
  }
}
define('product-results', ProductResults);
